import React, { useState } from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import { useInView } from '@/hooks/useInView';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactSection = ({ t }) => {
  const [ref, isInView] = useInView();
  const [formData, setFormData] = useState({ name: '', email: '', company: '', message: '' });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      await axios.post(`${API}/contact`, formData);
      setFormStatus({ type: 'success', message: t.contact.successMessage });
      setFormData({ name: '', email: '', company: '', message: '' });
    } catch {
      setFormStatus({ type: 'error', message: t.contact.errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-white" data-testid="contact-section">
      <div ref={ref} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-700 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="contact-title">{t.contact.title}</h2>
          <div className="w-20 h-1 bg-blue-600 mx-auto mb-4"></div>
          <p className="text-xl text-gray-600" data-testid="contact-subtitle">{t.contact.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6" data-testid="contact-info-title">{t.contact.infoTitle}</h3>
            <div className="space-y-6">
              <div className="flex items-start" data-testid="contact-phone">
                <div className="bg-blue-100 p-3 rounded-lg mr-4">
                  <Phone className="text-blue-600" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">{t.contact.phone}</h4>
                  <p className="text-gray-600">+27 11 712 0000</p>
                </div>
              </div>

              <div className="flex items-start" data-testid="contact-email">
                <div className="bg-blue-100 p-3 rounded-lg mr-4">
                  <Mail className="text-blue-600" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">{t.contact.email}</h4>
                  <p className="text-gray-600">info@anlumax.co.za</p>
                </div>
              </div>

              <div className="flex items-start" data-testid="contact-address">
                <div className="bg-blue-100 p-3 rounded-lg mr-4">
                  <MapPin className="text-blue-600" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">{t.contact.address}</h4>
                  <p className="text-gray-600">
                    Avenues Office Park, Oak House<br />
                    6 Mellis Road, Rivonia, 2128<br />
                    South Africa
                  </p>
                </div>
              </div>

              <div className="mt-8 p-6 bg-blue-50 rounded-xl">
                <h4 className="font-semibold text-gray-900 mb-2" data-testid="company-unit-title">{t.contact.businessUnit}</h4>
                <p className="text-gray-700">
                  {t.contact.businessUnitText}<br />
                  <span className="font-semibold">CGES - Group Ecosystem Services</span>
                </p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6" data-testid="contact-form-title">{t.contact.formTitle}</h3>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">{t.contact.nameLabel} *</label>
                <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                  placeholder={t.contact.namePlaceholder} data-testid="contact-form-name" />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">{t.contact.emailLabel} *</label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                  placeholder={t.contact.emailPlaceholder} data-testid="contact-form-email" />
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">{t.contact.companyLabel}</label>
                <input type="text" id="company" name="company" value={formData.company} onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all"
                  placeholder={t.contact.companyPlaceholder} data-testid="contact-form-company" />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">{t.contact.messageLabel} *</label>
                <textarea id="message" name="message" value={formData.message} onChange={handleInputChange} required rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all resize-none"
                  placeholder={t.contact.messagePlaceholder} data-testid="contact-form-message"></textarea>
              </div>

              {formStatus.message && (
                <div className={`p-4 rounded-lg ${formStatus.type === 'success' ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-red-100 text-red-800 border border-red-300'}`}
                  data-testid="contact-form-status">
                  {formStatus.message}
                </div>
              )}

              <button type="submit" disabled={isSubmitting}
                className="w-full bg-blue-600 text-white py-4 rounded-lg font-semibold hover:bg-blue-700 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                data-testid="contact-form-submit">
                {isSubmitting ? t.contact.submittingBtn : t.contact.submitBtn}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
